// Dilim yöntemi güvenlik katsayısı: Bishop basitleştirilmiş (iterasyonlu) ve Fellenius (İsveç dilim) yöntemleri.
// Statik-eşdeğer deprem: her dilime kh·W yatay kuvveti, momenti merkeze göre (yc kolu); kv yok.
import type { SlopeMethod } from "../types/result";
import type { SliceArrays } from "./slices";

export interface FsOptions { method: SlopeMethod; kh: number; cy: number; r: number }
export interface FsResult { fs: number; driving: number; resisting: number; iterations: number }

const M_ALPHA_MIN = 0.1;    // mα çok küçülünce (dik taban, yüksek φ/F) Bishop sayısal olarak bozulur; alt sınır
const MAX_ITER = 100;
const TOL = 1e-5;

/** Kaydırıcı toplam (R'ye bölünmüş): ΣW·sinα + Σkh·W·(cy − yc)/R */
function drivingSum(s: SliceArrays, kh: number, cy: number, r: number): number {
  let d = 0;
  for (let i = 0; i < s.n; i++) d += s.W[i]! * s.sinA[i]! + (kh > 0 ? (kh * s.W[i]! * (cy - s.yc[i]!)) / r : 0);
  return d;
}

/** Fellenius: F = Σ[c·l + (W·cosα − kh·W·sinα − u·l)·tanφ] / D, l = b/cosα */
export function felleniusFs(s: SliceArrays, kh: number, cy: number, r: number): FsResult {
  const D = drivingSum(s, kh, cy, r);
  if (D <= 1e-9) return { fs: Infinity, driving: D, resisting: Infinity, iterations: 0 };
  let R = 0;
  for (let i = 0; i < s.n; i++) {
    const l = s.b / Math.max(s.cosA[i]!, 1e-6);
    const N = Math.max(0, s.W[i]! * s.cosA[i]! - kh * s.W[i]! * s.sinA[i]! - s.u[i]! * l);
    R += s.c[i]! * l + N * s.tanPhi[i]!;
  }
  return { fs: R / D, driving: D, resisting: R, iterations: 1 };
}

/** Bishop basitleştirilmiş: F = Σ[(c·b + (W − u·b)·tanφ)/mα] / D, mα = cosα + sinα·tanφ/F (ardışık yaklaşım) */
export function bishopFs(s: SliceArrays, kh: number, cy: number, r: number): FsResult {
  const D = drivingSum(s, kh, cy, r);
  if (D <= 1e-9) return { fs: Infinity, driving: D, resisting: Infinity, iterations: 0 };
  let F = Math.max(0.5, Math.min(felleniusFs(s, kh, cy, r).fs, 5));
  let R = 0, it: number;
  for (it = 1; it <= MAX_ITER; it++) {
    R = 0;
    for (let i = 0; i < s.n; i++) {
      const m = Math.max(s.cosA[i]! + (s.sinA[i]! * s.tanPhi[i]!) / F, M_ALPHA_MIN);
      const Neff = Math.max(0, s.W[i]! - s.u[i]! * s.b);
      R += (s.c[i]! * s.b + Neff * s.tanPhi[i]!) / m;
    }
    const Fn = R / D;
    if (Math.abs(Fn - F) < TOL) { F = Fn; break; }
    F = Fn;
  }
  return { fs: F, driving: D, resisting: R, iterations: it };
}

export function factorOfSafety(s: SliceArrays, o: FsOptions): FsResult {
  return o.method === "fellenius" ? felleniusFs(s, o.kh, o.cy, o.r) : bishopFs(s, o.kh, o.cy, o.r);
}
